<template>
  <div>
    <v-row
      dense
      align="end"
    >
      <v-col>
        <validation-provider
          v-slot="{ errors }"
        >
          <number-input
            v-model="dirtySetParams.NMRP"
            outlined
            dense
            :label="$t('training_builder.text_input.num_of_repetitions.title')"
            :placeholder="$t('label.e_g') + ' ' + 10"
            :decimal-precision="0"
            :error-messages="errors"
          />
        </validation-provider>
      </v-col>
      <v-col cols="6">
        <minute-second-input
          :value="dirtySetParams.RSSTDR"
          :label="$t('training_builder.text_input.rest_time_after_set')"
          variable="RSSTDR"
          @input="handleDurationInput"
        />
      </v-col>
    </v-row>

    <v-divider class="mb-6" />

    <v-row no-gutters>
      <v-col
        class="mb-2 ml-1 text-body-1"
        cols="12"
      >
        {{ $t('training_builder.configure.dropdown.prescriptor.title') }}
      </v-col>
      <v-col cols="6">
        <validation-provider
          v-slot="{ errors }"
        >
          <v-select
            v-model="dirtySetParams.PRSCR"
            outlined
            dense
            :error-messages="errors"
            :items="[ { text: $t('picker.time.title'), value: 'time' }, { text: $t('table_headers.performance.player_details.efforts.distance'), value: 'distance' }]"
          />
        </validation-provider>
      </v-col>
    </v-row>

    <div v-if="dirtySetParams.PRSCR">
      <v-row
        v-if="dirtySetParams.PRSCR === 'time'"
        dense
      >
        <v-col>
          <minute-second-input
            :value="dirtySetParams.DR1"
            :label="$t('training_builder.configure.prescriptor.duration') + ' 1'"
            variable="DR1"
            @input="handleDurationInput"
          />
        </v-col>
        <v-col>
          <minute-second-input
            :value="dirtySetParams.DR2"
            :label="$t('training_builder.configure.prescriptor.duration') + ' 2'"
            variable="DR2"
            @input="handleDurationInput"
          />
        </v-col>
      </v-row>
      <v-row
        v-else-if="dirtySetParams.PRSCR === 'distance'"
        dense
      >
        <v-col>
          <kilometer-meter-input
            :value="dirtySetParams.DIST1"
            variable="DIST1"
            :label="$t('training_builder.text_input.distance.title') + ' 1'"
            @input="dirtySetParams.DIST1 = $event"
          />
        </v-col>
        <v-col>
          <kilometer-meter-input
            :value="dirtySetParams.DIST2"
            variable="DIST2"
            :label="$t('training_builder.text_input.distance.title') + ' 2'"
            @input="dirtySetParams.DIST2 = $event"
          />
        </v-col>
      </v-row>
      <v-row
        dense
      >
        <v-col>
          <validation-provider
            v-slot="{ errors }"
          >
            <number-input
              :value="displayedSPD1"
              outlined
              :label="$t('training_builder.groups.speed') + ' 1'"
              :placeholder="$t('label.e_g') + ' ' + 10"
              dense
              :suffix="$t(`suffixes.${getUnitSuffixForUser('km/h')}`)"
              :decimal-precision="1"
              :error-messages="errors"
              @input="displayedSPD1 = $event"
            />
          </validation-provider>
        </v-col>
        <v-col>
          <validation-provider
            v-slot="{ errors }"
          >
            <number-input
              :value="displayedSPD2"
              outlined
              :label="$t('training_builder.groups.speed') + ' 2'"
              :placeholder="$t('label.e_g') + ' ' + 10"
              dense
              :suffix="$t(`suffixes.${getUnitSuffixForUser('km/h')}`)"
              :decimal-precision="1"
              :error-messages="errors"
              @input="displayedSPD2 = $event"
            />
          </validation-provider>
        </v-col>
      </v-row>
    </div>
  </div>
</template>

<script>
import NumberInput from '@/components/helpers/NumberInput.vue'
import { cloneVariables, convertSpeedByUnitSystem } from '@/components/training-builder/helpers'
import MinuteSecondInput from '@/components/helpers/MinuteSecondInput.vue'
import KilometerMeterInput from '@/components/helpers/KilometerMeterInput.vue'
import { getUnitSuffixForUser } from '@/utils/helpers'
import { cloneDeep } from 'lodash'

export default {
  name: 'EnduranceParamsEntry',
  components: { KilometerMeterInput, MinuteSecondInput, NumberInput },
  props: {
    exercise: {
      type: Object,
      required: true
    },
    setParams: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      dirtySetParams: cloneVariables(this.setParams),
      internalSPD1: null,
      internalSPD2: null
    }
  },
  computed: {
    user() {
      return this.$store.getters[this.$storeGetters.AUTH_USER]
    },
    displayedSPD1: {
      get() {
        return convertSpeedByUnitSystem(this.internalSPD1, this.user.organisation_settings.unit_system, true)
      },
      set(value) {
        this.dirtySetParams.SPD1 = convertSpeedByUnitSystem(value, this.user.organisation_settings.unit_system, false)
      }
    },
    displayedSPD2: {
      get() {
        return convertSpeedByUnitSystem(this.internalSPD2, this.user.organisation_settings.unit_system, true)
      },
      set(value) {
        this.dirtySetParams.SPD2 = convertSpeedByUnitSystem(value, this.user.organisation_settings.unit_system, false)
      }
    }
  },
  watch: {
    dirtySetParams: {
      deep: true,
      handler: function (newValue) {
        this.$emit('update:setParams', newValue)
      }
    },
    'dirtySetParams.PRSCR': function (val) {
      if (val === 'distance') {
        this.dirtySetParams.DR1 = null
        this.dirtySetParams.DR2 = null
      } else if (val === 'duration') {
        this.dirtySetParams.DIST1 = null
        this.dirtySetParams.DIST2 = null
      }
    }
  },
  mounted() {
    if (!this.dirtySetParams.PRSCR) this.dirtySetParams.PRSCR = 'time'

    this.internalSPD1 = cloneDeep(this.setParams.SPD1)
    this.internalSPD2 = cloneDeep(this.setParams.SPD2)
  },
  methods: {
    getUnitSuffixForUser,
    checkIfParamExists(param) {
      if (!this.exercise.tags) return false

      return this.exercise.tags.some(t => t.tag_id === 'params' && t.value === param)
    },
    handleDurationInput(min, sec, variable) {
      this.dirtySetParams[variable] = min * 60 + sec > 0 ? min * 60 + sec : null
    }
  }
}
</script>
